import { useState } from 'react';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { founderDashboardAPI } from '../../../../services/api';
import {
  PageHeader,
  PageState,
  StaleNotice,
  StatCard,
  StatusBadge,
} from './OutreachShared';
import {
  focusRing,
  formatDateTime,
  humanize,
  listFrom,
  useOutreachResource,
} from './outreachUtils';

const PAGE_SIZE = 25;

export default function OutreachSuppressionsPage() {
  const [offset, setOffset] = useState(0);
  const resource = useOutreachResource(
    () => founderDashboardAPI.getOutreachSuppressions({ limit: PAGE_SIZE, offset }),
    [offset]
  );
  const rows = listFrom(resource.data, 'suppressions', 'preferences', 'items', 'rows');
  const total = Number(resource.data?.pagination?.total ?? resource.data?.total ?? rows.length);
  const counts = {
    off: rows.filter((row) => row.lifecycleEnabled === false || row.desiredState === 'disabled')
      .length,
    providerSuppressed: rows.filter(
      (row) => row.providerLifecycleSuppressed || row.providerGlobalSuppressed
    ).length,
    syncing: rows.filter((row) => row.providerSyncStatus === 'pending').length,
    safetyBlocked: rows.filter(
      (row) => row.hardBounced || row.spamReported || row.manuallyBlocked
    ).length,
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Preferences & suppressions"
        description="Read-only, masked evidence for the local lifecycle preference and SendGrid suppression state."
        refreshing={resource.refreshing}
        onRefresh={resource.reload}
      />
      <StaleNotice
        generatedAt={resource.data?.generatedAt || resource.data?.generated_at}
        loadedAt={resource.loadedAt}
      />

      <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <ShieldExclamationIcon className="h-5 w-5 flex-none" />
        <p>
          Recipient intent is authoritative. This founder view cannot re-enable a preference,
          remove a provider suppression, or bypass a complaint, bounce, or manual block.
        </p>
      </div>

      <PageState
        loading={resource.loading}
        error={resource.error}
        permissionDenied={resource.permissionDenied}
        onRetry={resource.reload}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Lifecycle off"
            value={counts.off}
            detail="On this page · local eligibility disabled"
          />
          <StatCard
            label="Provider suppressed"
            value={counts.providerSuppressed}
            detail="On this page · provider suppression"
          />
          <StatCard
            label="Syncing"
            value={counts.syncing}
            detail="On this page · fails closed"
            tone={counts.syncing > 0 ? 'warning' : 'default'}
          />
          <StatCard
            label="Safety blocked"
            value={counts.safetyBlocked}
            detail="On this page · complaint, bounce, or manual block"
            tone={counts.safetyBlocked > 0 ? 'danger' : 'default'}
          />
        </div>

        <PageState
          loading={false}
          empty={rows.length === 0}
          emptyTitle="No preference or suppression evidence"
          emptyMessage="Masked records will appear after recipient intent or provider evidence is recorded."
        >
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-[980px] w-full text-left text-sm">
                <caption className="sr-only">
                  Masked lifecycle email preference and suppression records
                </caption>
                <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Masked identity</th>
                    <th className="px-4 py-3">Logical preference</th>
                    <th className="px-4 py-3">Provider state</th>
                    <th className="px-4 py-3">Safety</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Last intent</th>
                    <th className="px-4 py-3">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row, index) => (
                    <tr key={row.id || row.preferenceId || `${offset}-${index}`} className="align-top">
                      <td className="px-4 py-4 font-medium text-gray-900">
                        {row.maskedIdentity ||
                          row.masked_identity ||
                          row.recipientMasked ||
                          (row.addressHashPrefix ? `Hash ${row.addressHashPrefix}…` : 'Masked')}
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge
                          value={
                            row.preferenceState ||
                            row.preference_state ||
                            row.lifecyclePreference ||
                            row.lifecycle_preference ||
                            row.desiredState ||
                            'unknown'
                          }
                        />
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge
                          value={
                            row.providerState ||
                            row.provider_state ||
                            row.suppressionState ||
                            row.suppression_state ||
                            row.providerSyncStatus ||
                            'unknown'
                          }
                        />
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge
                          value={
                            row.safetyState ||
                            row.safety_state ||
                            (row.safetyBlocked ||
                            row.safety_blocked ||
                            row.hardBounced ||
                            row.spamReported ||
                            row.manuallyBlocked
                              ? 'blocked'
                              : 'healthy')
                          }
                        />
                      </td>
                      <td className="px-4 py-4 text-gray-700">
                        {humanize(
                          row.intentSource ||
                            row.intent_source ||
                            row.source ||
                            'preference record'
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                        {formatDateTime(
                          row.intentAt ||
                            row.intent_at ||
                            row.updatedAt ||
                            row.updated_at
                        )}
                      </td>
                      <td className="max-w-xs px-4 py-4 text-gray-700">
                        {humanize(
                          row.reasonCode ||
                            row.reason_code ||
                            row.blockedReason ||
                            row.blocked_reason
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-500">
              Showing {offset + 1}–{Math.min(offset + rows.length, total)} of {total}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
                className={`min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${focusRing}`}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={offset + rows.length >= total}
                onClick={() => setOffset((value) => value + PAGE_SIZE)}
                className={`min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${focusRing}`}
              >
                Next
              </button>
            </div>
          </div>
        </PageState>
      </PageState>
    </div>
  );
}
